import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {actions, selectors} from '../../Redux/index';
import {i18n, i18nString} from '../../Utility/Format';
import classnames from 'classnames';

import DrawerPanel, {
    DrawerPanelPanel,
    DrawerPanelPanelHeader,
    DrawerPanelPanelContent
} from '../../Components/Helper/DrawerPanel';
import Backdrop from '../../Components/Helper/Backdrop';
import PanelToggleButton from '../../Components/Controls/PanelToggleButton';
import Button from '../../Components/Controls/Button';
import Icon from '../../Components/Helper/Icon';
import HomeOptions from '../../Components/Options/HomeOptions';
import SettlementOptions from '../../Components/Options/SettlementOptions';
import SpotOptions from '../../Components/Options/SpotOptions';
import ForwardOptions from '../../Components/Options/ForwardOptions';
import PanelHeader from '../../Components/Content/ApplicationHeader';

/**
 * Control for toggeling the `StageOptionsPanel` from anywhere in the application
 */
const __StageOptionsToggle = (props) => {
    const optionToggleClassNames = classnames('stage-options-toggle');

    if (props.isClosingButton) {
        return <PanelToggleButton icon="close" onClick={props.onToggleMenu} aria-label={i18nString('dbm.button.close', props.locale)}/>
    }
    return (
        <Button
            className={optionToggleClassNames}
            onClick={props.onToggleMenu}
            aria-label={i18nString('dbm.button.options', props.locale)}
        >
            <Icon name="options" isCollapsed={true}/>
            <span className="stage-options-toggle__label">{i18n('dbm.button.options')}</span>
        </Button>
    );
};

__StageOptionsToggle.propTypes={
    isClosingButton: PropTypes.bool,
    onToggleMenu: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired
};

const mapDispatchToProps = (dispatch) => {
    return {
        onToggleMenu: (e) => {
            if (e) {
                e.stopPropagation();
            }
            dispatch(actions.UI.OptionsMenu.toggleOptionsMenu());
        },
        onChangeOptionsForHome: (options) => {
            dispatch(actions.UI.HomeOptions.changeHomeOptions(options));
        },
        onSaveAndClose: () => {
            dispatch(actions.UI.OptionsMenu.toggleOptionsMenu());
            dispatch(actions.Data.Reload.sagaReloadData()); // Soft reload instead of hard browser refresh
        },
        onSaveOptionsForHome: (options) => {
            dispatch(actions.UI.HomeOptions.changeHomeOptions(options)); // This will trigger the LocalStorageSaga and update the state
        },
        onChangeOptionsForSpot: (options) => {
            dispatch(actions.UI.SpotOptions.changeSpotOptions(options));
        },
        onSaveOptionsForSpot: (options) => {
            dispatch(actions.UI.SpotOptions.changeSpotOptions(options)); // This will trigger the LocalStorageSaga and update the state
        },
        onChangeOptionsForForward: (options) => {
            dispatch(actions.UI.ForwardOptions.changeForwardOptions(options));
        },
        onSaveOptionsForForward: (options) => {
            dispatch(actions.UI.ForwardOptions.changeForwardOptions(options)); // This will trigger the LocalStorageSaga and update the state
        },
        onChangeOptionsForSettlement: (options) => {
            dispatch(actions.UI.SettlementOptions.changeSettlementOptions(options));
        },
        onSaveOptionsForSettlement: (options) => {
            dispatch(actions.UI.SettlementOptions.changeSettlementOptions(options)); // This will trigger the LocalStorageSaga and update the state
        },
    }
};

const mapStateToProps = (state) => {
    return {
        homeOptions: selectors.UI.HomeOptions.getHomeOptions(state),
        spotOptions: selectors.UI.SpotOptions.getSpotOptions(state),
        forwardOptions: selectors.UI.ForwardOptions.getForwardOptions(state),
        settlementOptions: selectors.UI.SettlementOptions.getSettlementOptions(state),

        isOpen: selectors.UI.OptionsMenu.isMenuOpen(state),
        isValueDateVisible: selectors.UI.OptionsMenu.isValueDateVisible(state),
        location: selectors.Router.locationBeforeTransitions(state).location,
        currencies: selectors.Data.Currency.getCurrencies(state),
        locale: selectors.UI.Locale.getLocale(state)
    };
};

const createOptions = (props) => {
    if (!props.location || !props.currencies || props.currencies.length === 0) {
        return null;
    }

    if (props.location.pathname === '/') {
        return (
            <HomeOptions
                currencies={props.currencies}
                options={props.homeOptions}
                onCcyPairsChange={(changedCcyPairs) => {
                    const changedOptions = Object.assign({}, props.homeOptions, {ccyPairs: changedCcyPairs});
                    props.onChangeOptionsForHome(changedOptions);
                }}
                onMenuToggle={(e, options) => {
                    props.onSaveOptionsForHome(options); // Save the options
                    props.onSaveAndClose(); // Close the panel and reload
                }}
                onReset={() => {
                    props.onChangeOptionsForHome({});
                }}
                locale={props.locale}
            />
        );
    } else if (props.location.pathname === '/spot') {
        return (
            <SpotOptions
                currencies={props.currencies}
                options={props.spotOptions}
                onCcyPairsChange={(changedCcyPairs) => {
                    const changedOptions = Object.assign({}, props.spotOptions, {ccyPairs: changedCcyPairs});
                    props.onChangeOptionsForSpot(changedOptions);
                }}
                onGroupsChange={(changedGroups) => {
                    const changedOptions = Object.assign({}, props.spotOptions, {groups: changedGroups});
                    props.onChangeOptionsForSpot(changedOptions);
                }}
                onMenuToggle={(e, options) => {
                    props.onSaveOptionsForSpot(options); // Save the options
                    props.onSaveAndClose(); // Close the panel and reload
                }}
                onReset={() => {
                    props.onChangeOptionsForSpot({});
                }}
                locale={props.locale}
            />
        );
    } else if (props.location.pathname === '/forward') {
        return (
            <ForwardOptions
                currencies={props.currencies}
                options={props.forwardOptions}
                locale={props.locale}
                onCcyPairsChange={(changedCcyPairs) => {
                    const changedOptions = Object.assign({}, props.forwardOptions, {ccyPairs: changedCcyPairs});
                    props.onChangeOptionsForForward(changedOptions);
                }}
                onTenorsChange={(changedTenors) => {
                    const changedOptions = Object.assign({}, props.forwardOptions, {tenors: changedTenors});
                    props.onChangeOptionsForForward(changedOptions);
                }}
                onBrokenDateAdd={(date) => {
                    const brokenDates = props.forwardOptions.brokenDates.slice();
                    brokenDates.push(date);
                    const changedOptions = Object.assign({}, props.forwardOptions, {brokenDates: brokenDates});
                    props.onChangeOptionsForForward(changedOptions);
                }}
                onBrokenDateRemove={(date) => {
                    const brokenDates = props.forwardOptions.brokenDates.filter((it) => {
                        return it !== date;
                    })
                    const changedOptions = Object.assign({}, props.forwardOptions, {brokenDates: brokenDates});
                    props.onChangeOptionsForForward(changedOptions);
                }}
                onSwapPointsToggle={(swapPoints) => {
                    const changedOptions = Object.assign({}, props.forwardOptions, {swapPoints: swapPoints});
                    props.onChangeOptionsForForward(changedOptions);
                }}
                onMenuToggle={(e, options) => {
                    props.onSaveOptionsForForward(options); // Save the options
                    props.onSaveAndClose(); // Close the panel and reload
                }}
                onReset={() => {
                    props.onChangeOptionsForForward({});
                }}
            />
        );
    } else if (props.location.pathname === '/settlement') {
        return (
            <SettlementOptions
                options={props.settlementOptions}
                locale={props.locale}
                onFixingDateSelect={(date) => {
                    const changedOptions = Object.assign({}, props.settlementOptions, {date: date});
                    props.onChangeOptionsForSettlement(changedOptions);
                }}
                onFixingTypeChange={(type) => {
                    const changedOptions = Object.assign({}, props.settlementOptions, {type: type});
                    props.onChangeOptionsForSettlement(changedOptions);
                }}
                onMenuToggle={(e, options) => {
                    props.onSaveOptionsForSettlement(options); // Save the options
                    props.onSaveAndClose(); // Close the panel and reload
                }}
                onReset={() => {
                    props.onChangeOptionsForSettlement({});
                }}
            />
        );
    } else {
        return null;
    }
};

const createHeader = (props) => {
    if (!props.location || !props.location.pathname) {
        return <h2>{i18n('dbm.home.options.label')}</h2>;
    }

    const page = props.location.pathname.substr(1) || 'home';
    const key = 'dbm.' + page + '.options.label';
    return <h2>{i18n(key)}</h2>
};

/**
 * Displays the options for each section of the application. e.g. the options for "Settlement".
 * The content for the panel and the panel header can be injected independently via a portal.
 */
const StageOptionsPanel = (props) => {
    return (
        <DrawerPanel onClick={props.onToggleMenu} isOpen={props.isOpen} orientation="right">
            <DrawerPanelPanel orientation="right">
                <DrawerPanelPanelHeader>
                    <PanelHeader
                        left={ createHeader(props) }
                    />
                </DrawerPanelPanelHeader>
                <DrawerPanelPanelContent>
                    { createOptions(props) }
                </DrawerPanelPanelContent>
            </DrawerPanelPanel>
            <Backdrop onClick={props.onToggleMenu}/>
        </DrawerPanel>
    );
};

StageOptionsPanel.propTypes={
    isOpen: PropTypes.bool.isRequired
};

const _StageOptionsPanel = connect(mapStateToProps, mapDispatchToProps)(StageOptionsPanel);
const _StageOptionsToggle = connect(mapStateToProps, mapDispatchToProps)(__StageOptionsToggle);

export default _StageOptionsPanel;
export {_StageOptionsToggle as StageOptionsToggle};